import React from 'react';
import type { HighlightedProps, SchoolReportType } from '../../types';
import type { DateRange } from '../../../util/types';
import type { ChartDataType, ChartIconType } from '../../../components/charts/types';
import type { DurationUnit } from '../../../util/dates';
import * as d3 from 'd3';
import { useI18n } from '@whizz/react-i18n';
import { SectionLayout } from '../modules';
import { StudentProgress } from '../../../components';
import { YEAR_IN_WEEKS, convertWeeksToYears } from '../../../util/dates';
import { DateTime, Interval } from 'luxon';

type Props = {
  schoolReport: SchoolReportType;
  showOthersData: boolean
  iconToUse: ChartIconType
  dateRange?: DateRange
} & HighlightedProps;

const SchoolMathsAgeImprovements = ({ schoolReport, showOthersData, highlightedLabel, highlightedIds, iconToUse, dateRange, onElementClick }: Props): JSX.Element => {
  const { translate } = useI18n();

  const dataToUse = (): ChartDataType[] => {
    if (iconToUse === 'class') {
      return schoolReport.classes;
    } else return schoolReport.yearGroups;
  };

  const getHeaderValue = (groups: ChartDataType[]): number => {
    let groupList = groups;
    if (highlightedIds.length > 0) {
      groupList = groupList.filter((group) => highlightedIds.includes(group.id));
    }
    const mean = d3.mean(groupList.map((group) => group.chartsData?.mathsAgeImprovement));
    return mean != undefined ? mean : 0;
  };

  const calculateWeeksToYears = (value: number | null, useUnits?: DurationUnit): [ number | null, DurationUnit ] => {
    /**
    * If the value (number of weeks) is greater than 52 (1 year) convert value and units to 'year'
    */
    let unit: DurationUnit = 'unitWeek';
    if (value === null) {
      return [ value, 'unitWeek' ];
    }
    let convertToYears = value > YEAR_IN_WEEKS || (useUnits && useUnits === 'unitYear');
    if (useUnits && useUnits === 'unitWeek') {
      convertToYears = false;
    }
    if (convertToYears) {
      value = convertWeeksToYears(value);
      unit = 'unitYear';
    } else {
      // Less than 1 year, so format number (weeks) to 1 decimal places
      value = Number(value.toFixed(1));
      unit = 'unitWeek';
    }
    return [ value, unit ];
  };

  let allGroups = dataToUse();
  const highlightValue = getHeaderValue(allGroups);
  const [ value, valueUnit ] = calculateWeeksToYears(Number(schoolReport.average.mathsAgeImprovementAverage));
  const [ highlightValueOrNull, highlightUnit ] = calculateWeeksToYears(highlightValue, showOthersData ? valueUnit : undefined);

  const convertToYears = highlightValueOrNull !== null && highlightUnit === 'unitYear' || highlightValueOrNull === null && valueUnit === 'unitYear';

  if (convertToYears) {

    // Convert the `mathsAgeImprovement` to years
    allGroups = allGroups.map((group) => {
      return ({
        ...group,
        chartsData: {
          ...group.chartsData,
          mathsAgeImprovement: group.chartsData?.mathsAgeImprovement ? convertWeeksToYears(group.chartsData?.mathsAgeImprovement) : 0,
        },
      });
    }) as ChartDataType[];
  }

  // Calculate the number of weeks in the date range
  const dateRangeInterval = (dateRange && DateTime.isDateTime(dateRange.startDate!)) ? Interval.fromDateTimes(dateRange.startDate!, dateRange.endDate ?? DateTime.now()) : null;
  const numberOfWeeks = dateRangeInterval ? Math.round(dateRangeInterval?.toDuration('weeks').weeks) : null;

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.title')}
      value={showOthersData ? value?.toFixed(2) : null}
      highlightLabel={highlightedLabel}
      highlightValue={highlightValueOrNull?.toFixed(2)}
      unit={translate(`pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.${highlightValueOrNull ? highlightUnit : valueUnit}`, { count: value ?? 0 })}
      moreInfo={translate('pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.info')}
      id={'school-maths-age-improvements-container'}
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={allGroups}
        chartType={'mathsAgeImprovement'}
        type={iconToUse}
        highlightedIds={highlightedIds}
        excellentBreaking={numberOfWeeks}
        thinAxis={numberOfWeeks === null}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

SchoolMathsAgeImprovements.displayName = 'SchoolMathsAgeImprovements';

export default SchoolMathsAgeImprovements;
